/**
 * AccountModal - アカウント管理モーダル
 *
 * 機能:
 * - 匿名ユーザー: アカウント連携（メール登録）
 * - 未ログイン: ログイン/新規登録
 * - ログイン済み: アカウント情報表示、ログアウト
 */

import React, { useState } from 'react';
import gameStyles from '../styles/gameStyles';

// 型安全なスタイル参照
const styles = {
  modal: gameStyles.modal as React.CSSProperties,
  modalContent: gameStyles.modalContent as React.CSSProperties,
  actionButton: gameStyles.actionButton as React.CSSProperties,
};

// ========================================
// 型定義
// ========================================

interface AccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  // 認証状態
  isAuthenticated: boolean;
  isAnonymous: boolean;
  session: unknown | null; // セッションオブジェクト
  profile: {
    id?: string;
    display_name?: string | null;
    email?: string;
    created_at?: string;
    is_anonymous?: boolean;
  } | null;
  // 認証アクション
  signUpWithEmail: (email: string, password: string, displayName?: string) => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  linkAccount: (email: string, password: string, displayName?: string) => Promise<void>;
  signOut: () => Promise<void>;
  // ローディング
  loading?: boolean;
}

type ModalMode = 'info' | 'login' | 'signup' | 'link';

// ========================================
// コンポーネント
// ========================================

const AccountModal: React.FC<AccountModalProps> = ({
  isOpen,
  onClose,
  isAuthenticated,
  isAnonymous,
  session,
  profile,
  signUpWithEmail,
  signInWithEmail,
  linkAccount,
  signOut,
  loading = false,
}) => {
  // ========================================
  // State
  // ========================================
  const [mode, setMode] = useState<ModalMode>('info');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // ========================================
  // ハンドラー
  // ========================================

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setError(null);
    setShowSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    setMode('info');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (mode === 'login') {
        await signInWithEmail(email, password);
        setShowSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else if (mode === 'signup') {
        await signUpWithEmail(email, password, displayName || undefined);
        setShowSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else if (mode === 'link') {
        // セッションがない場合はエラー
        if (!session) {
          throw new Error('セッションがありません。ページを再読み込みしてから再度お試しください。');
        }
        await linkAccount(email, password, displayName || undefined);
        setShowSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    setIsSubmitting(true);
    try {
      await signOut();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ログアウトに失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ========================================
  // レンダリング
  // ========================================

  if (!isOpen) return null;

  const modalContentStyle: React.CSSProperties = {
    ...styles.modalContent,
    maxWidth: '400px',
    width: '90%',
    padding: '24px',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    borderRadius: '8px',
    border: '2px solid rgba(107,76,230,0.3)',
    background: 'rgba(20,20,40,0.8)',
    color: '#fff',
    marginBottom: '12px',
    boxSizing: 'border-box',
  };

  const buttonStyle: React.CSSProperties = {
    ...styles.actionButton,
    width: '100%',
    padding: '14px',
    fontSize: '16px',
    marginTop: '8px',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: 'transparent',
    border: '1px solid rgba(107,76,230,0.5)',
    color: '#a0a0c0',
  };

  const linkButtonStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: '#6b9eff',
    cursor: 'pointer',
    fontSize: '13px',
    padding: '8px',
    textDecoration: 'underline',
  };

  // 成功メッセージ
  if (showSuccess) {
    return (
      <div style={styles.modal}>
        <div style={modalContentStyle}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
            <h2 style={{ color: '#4caf50', fontSize: '20px', marginBottom: '8px' }}>
              {mode === 'login' ? 'ログイン成功！' :
               mode === 'signup' ? '登録完了！' :
               'アカウント連携完了！'}
            </h2>
            <p style={{ color: '#a0a0a0', fontSize: '14px' }}>
              データは安全に保護されます
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 情報表示モード
  if (mode === 'info') {
    return (
      <div style={styles.modal}>
        <div style={modalContentStyle}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
          }}>
            <h2 style={{ color: '#e0e0e0', fontSize: '20px', margin: 0 }}>
              👤 アカウント
            </h2>
            <button
              onClick={handleClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#888',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '4px',
              }}
            >×</button>
          </div>

          {isAuthenticated && !isAnonymous ? (
            // ログイン済みユーザー
            <>
              <div style={{
                background: 'rgba(76,175,80,0.1)',
                border: '1px solid rgba(76,175,80,0.3)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                }}>
                  <span style={{ fontSize: '24px' }}>✅</span>
                  <span style={{ color: '#4caf50', fontWeight: 'bold' }}>
                    ログイン中
                  </span>
                </div>
                <div style={{ fontSize: '14px', color: '#d0d0d0' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ color: '#888' }}>名前: </span>
                    <span style={{ fontWeight: 'bold' }}>
                      {profile?.display_name || '未設定'}
                    </span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ color: '#888' }}>メール: </span>
                    <span>{(session as { user?: { email?: string } })?.user?.email || '---'}</span>
                  </div>
                  <div>
                    <span style={{ color: '#888' }}>登録日: </span>
                    <span>
                      {profile?.created_at
                        ? new Date(profile.created_at).toLocaleDateString('ja-JP')
                        : '---'}
                    </span>
                  </div>
                </div>
              </div>

              <p style={{
                fontSize: '13px',
                color: '#888',
                textAlign: 'center',
                marginBottom: '16px',
              }}>
                データはクラウドに安全に保存されています
              </p>

              <button
                onClick={handleSignOut}
                disabled={isSubmitting}
                style={secondaryButtonStyle}
              >
                {isSubmitting ? 'ログアウト中...' : 'ログアウト'}
              </button>
            </>
          ) : (
            // 匿名ユーザーまたは未ログイン
            <>
              <div style={{
                background: 'rgba(255,152,0,0.1)',
                border: '1px solid rgba(255,152,0,0.3)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                }}>
                  <span style={{ fontSize: '24px' }}>⚠️</span>
                  <span style={{ color: '#ff9800', fontWeight: 'bold' }}>
                    ゲストモード
                  </span>
                </div>
                <p style={{ fontSize: '13px', color: '#d0d0d0', margin: 0 }}>
                  現在のデータはこのブラウザにのみ保存されています。
                  ブラウザのデータを消去すると、ゲームデータも失われます。
                </p>
              </div>

              <div style={{
                background: 'rgba(107,76,230,0.1)',
                border: '1px solid rgba(107,76,230,0.3)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px',
              }}>
                <h3 style={{
                  color: '#9d4ce6',
                  fontSize: '14px',
                  marginTop: 0,
                  marginBottom: '8px',
                }}>
                  📋 アカウント登録のメリット
                </h3>
                <ul style={{
                  fontSize: '13px',
                  color: '#a0a0c0',
                  margin: 0,
                  paddingLeft: '20px',
                }}>
                  <li>データがクラウドに安全に保存</li>
                  <li>別のデバイスからもプレイ可能</li>
                  <li>ブラウザデータ消去でも復元可能</li>
                </ul>
              </div>

              <button
                onClick={() => { resetForm(); setMode('link'); }}
                style={{
                  ...buttonStyle,
                  background: 'linear-gradient(135deg, #9d4ce6 0%, #6b4ce6 100%)',
                }}
              >
                📧 メールアドレスで登録
              </button>

              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '12px',
              }}>
                <button
                  onClick={() => { resetForm(); setMode('login'); }}
                  style={linkButtonStyle}
                >
                  既にアカウントをお持ちの方はこちら
                </button>
              </div>
            </>
          )}

          {error && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              background: 'rgba(244,67,54,0.1)',
              border: '1px solid rgba(244,67,54,0.3)',
              borderRadius: '8px',
              color: '#f44336',
              fontSize: '13px',
            }}>
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  // フォームモード（login, signup, link）
  const formTitle = mode === 'login' ? 'ログイン' :
                    mode === 'signup' ? '新規登録' :
                    'アカウント連携';
  const submitButtonText = mode === 'login' ? 'ログイン' :
                           mode === 'signup' ? '登録する' :
                           '連携する';

  return (
    <div style={styles.modal}>
      <div style={modalContentStyle}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => { resetForm(); setMode('info'); }}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#888',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '4px',
              }}
            >←</button>
            <h2 style={{ color: '#e0e0e0', fontSize: '20px', margin: 0 }}>
              {formTitle}
            </h2>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#888',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px',
            }}
          >×</button>
        </div>

        {mode === 'link' && (
          <div style={{
            background: 'rgba(76,175,80,0.1)',
            border: '1px solid rgba(76,175,80,0.3)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#4caf50',
          }}>
            ✅ 現在のゲームデータは引き継がれます
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {(mode === 'signup' || mode === 'link') && (
            <input
              type="text"
              placeholder="表示名（任意）"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              style={inputStyle}
              disabled={isSubmitting}
            />
          )}

          <input
            type="email"
            placeholder="メールアドレス"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={inputStyle}
            disabled={isSubmitting}
          />

          <input
            type="password"
            placeholder="パスワード（6文字以上）"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            style={inputStyle}
            disabled={isSubmitting}
          />

          {error && (
            <div style={{
              marginBottom: '12px',
              padding: '12px',
              background: 'rgba(244,67,54,0.1)',
              border: '1px solid rgba(244,67,54,0.3)',
              borderRadius: '8px',
              color: '#f44336',
              fontSize: '13px',
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              ...buttonStyle,
              background: isSubmitting
                ? 'rgba(107,76,230,0.5)'
                : 'linear-gradient(135deg, #9d4ce6 0%, #6b4ce6 100%)',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting ? '処理中...' : submitButtonText}
          </button>
        </form>

        {mode === 'login' && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '12px',
          }}>
            <button
              onClick={() => { resetForm(); setMode('signup'); }}
              style={linkButtonStyle}
            >
              アカウントを新規作成
            </button>
          </div>
        )}

        {mode === 'signup' && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '12px',
          }}>
            <button
              onClick={() => { resetForm(); setMode('login'); }}
              style={linkButtonStyle}
            >
              既にアカウントをお持ちの方
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountModal;
